import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore
} from 'react';

import { DeepPartial } from '@src/utils';

const deepMerge = <T,>(current: T, updates: DeepPartial<T>) => {
  for (const key in updates) {
    const value = updates[key];
    const currentValue = current[key];

    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      currentValue !== null &&
      currentValue !== undefined &&
      Object.keys(value).length > 0
    ) {
      current[key] = {
        ...currentValue,
        ...deepMerge(currentValue as T, value as Partial<T>)
      };
    } else if (currentValue !== value) {
      current[key] = value as typeof currentValue;
    }
  }

  return current;
};

export const createOptimizedContext = <Store,>(initialState: Store) => {
  const NOT_FOUND_MSG = 'Store should be called inside respective context!';

  function useStoreData(defaultState?: Partial<Store>) {
    const store = useRef(
      defaultState
        ? deepMerge(structuredClone(initialState), defaultState)
        : structuredClone(initialState)
    );
    const get = useCallback(() => store.current, []);

    const subscribers = useRef(new Set<() => void>());

    const set = useCallback((value: DeepPartial<Store>) => {
      store.current = {
        ...store.current,
        ...deepMerge(store.current, value)
      };
      subscribers.current.forEach(cb => cb());
    }, []);

    const subscribe = useCallback((cb: () => void) => {
      subscribers.current.add(cb);
      return () => subscribers.current.delete(cb);
    }, []);

    return useMemo(() => ({ get, set, subscribe }), [get, set, subscribe]);
  }

  const StoreContext = createContext<ReturnType<typeof useStoreData> | null>(
    null
  );

  function Provider({
    children,
    defaultState
  }: {
    children: React.ReactNode;
    defaultState?: Partial<Store>;
  }) {
    return (
      <StoreContext.Provider value={useStoreData(defaultState)}>
        {children}
      </StoreContext.Provider>
    );
  }

  function useStoreSelector<T>(selector: (store: Store) => T): T {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error(NOT_FOUND_MSG);
    }

    const state = useSyncExternalStore(store.subscribe, () =>
      selector(store.get())
    );

    return state;
  }

  function useStoreDispatch() {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error(NOT_FOUND_MSG);
    }

    return store.set;
  }

  function useStore(): ReturnType<typeof useStoreData> {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error(NOT_FOUND_MSG);
    }

    return store;
  }

  return { Provider, useStoreSelector, useStoreDispatch, useStore };
};
